import { action, computed, makeObservable } from "mobx";
import { MrbBaseEditViewStore } from "mrb/core";
import { MrbFieldSelectStore } from "mrb/components/select";
import { PodcastGroupItemEditForm } from "administration/podcast/forms";
import { PodcastCoverImageUploadViewStore } from "administration/podcast/stores";
import { podcastCategory } from "common/constants";
import { femaleContentLockedFields } from "common/utils";
import { map } from "lodash";

class AddPodcastGroupItemViewStore extends MrbBaseEditViewStore {
    podcastCategories = null;
    @computed get episodeSortTypeOptions() {
        return this.podcastGroupEditViewStore.episodeSortTypeOptions;
    }

    @computed get isPremiumDisabled() {
        return this.podcastGroupEditViewStore.form.$("isPremium").value;
    }

    @computed get showScheduledRelease() {
        return !this.form.$("isActive").value;
    }

    constructor(rootStore, { podcastGroupEditViewStore }) {
        super(rootStore, {
            name: "podcast-group-item",
            FormClass: PodcastGroupItemEditForm,
            redirectOnCreateSuccess: false,
        });
        this.podcastGroupEditViewStore = podcastGroupEditViewStore;
        this.routeStore = this.podcastGroupEditViewStore.routeStore;
        makeObservable(this);

        this.coverImageUploadViewStore = new PodcastCoverImageUploadViewStore(this.rootStore, {
            routeStore: this.podcastGroupEditViewStore.routeStore,
        });

        this.podcastCategorySelectStore = new MrbFieldSelectStore(
            {
                isSearchable: true,
                virtualized: false,
                isClearable: false,
                initFetch: false,
                textKey: "name",
                valueKey: "id",
                actions: {
                    find: async (searchPhrase) => {
                        this.podcastCategories = await this.routeStore.getPodcastCategories(searchPhrase);
                        return this.podcastCategories;
                    },
                    // Shiurim are hidden on the Kedayo and Kedayo Pink unless the admin switches them on.
                    onChange: (option) => {
                        if (option?.item?.slug === podcastCategory.shiur) {
                            this.turnOffKedayoVisibility();
                        }
                    },
                },
            },
            this.form.$("category")
        );

        // Female content switches off the toggles it locks rather than only disabling them.
        this.reaction(
            () => this.form.$("isFemale").value,
            (isFemale) => {
                if (isFemale) {
                    femaleContentLockedFields.podcast.forEach((field) => this.form.$(field).onChange(false));
                }
            }
        );

        this.reaction(
            () => this.podcastGroupEditViewStore.shiurCategorySelectedCount,
            () => this.turnOffKedayoVisibility()
        );

        this.podcastCategorySelectStore.setSelectedItem(
            this.podcastGroupEditViewStore.podcastCategorySelectStore.selectedItem
        );
        this.reaction(
            () => this.podcastGroupEditViewStore.form.$("category").value,
            () => {
                if (
                    this.podcastGroupEditViewStore.form.$("category").value !== null &&
                    this.podcastGroupEditViewStore.form.$("category").value !== this.form.$("category").value
                ) {
                    this.podcastCategorySelectStore.setSelectedItem(
                        this.podcastGroupEditViewStore.podcastCategorySelectStore.selectedItem
                    );
                }
            }
        );

        this.reaction(
            () => this.podcastGroupEditViewStore.form.$("isPremium").value,
            (isPremium) => {
                this.form.$("isPremium").set(isPremium);
            }
        );

        this.reaction(
            () => this.form.$("isPremium").value,
            (isPremium) => {
                if (isPremium) {
                    this.form.$("producerId").addIsRequiredRule();
                } else {
                    this.form.$("producerId").removeIsRequiredRule();
                }
            }
        );

        // A female sub-podcast keeps its App, Naki Bolt and Kedayo visibility off whatever the group does; only
        // the Kedayo Pink follows the group for it.
        this.reaction(
            () => this.podcastGroupEditViewStore.form.$("isVisibleInApp").value,
            (isVisibleInApp) => {
                if (!this.form.$("isFemale").value) {
                    this.form.$("isVisibleInApp").onChange(isVisibleInApp);
                }
            }
        );

        this.reaction(
            () => this.podcastGroupEditViewStore.form.$("isVisibleInEmbeddedApp").value,
            (isVisibleInEmbeddedApp) => {
                if (!this.form.$("isFemale").value) {
                    this.form.$("isVisibleInEmbeddedApp").onChange(isVisibleInEmbeddedApp);
                }
            }
        );

        this.reaction(
            () => this.podcastGroupEditViewStore.form.$("isVisibleInHandheldPinkApp").value,
            (isVisibleInHandheldPinkApp) => {
                this.form.$("isVisibleInHandheldPinkApp").onChange(isVisibleInHandheldPinkApp);
            }
        );

        this.reaction(
            () => this.podcastGroupEditViewStore.form.$("isVisibleInHandheldApp").value,
            (isVisibleInHandheldApp) => {
                if (!this.form.$("isFemale").value) {
                    this.form.$("isVisibleInHandheldApp").onChange(isVisibleInHandheldApp);
                }
            }
        );

        this.genreSelectStore = new MrbFieldSelectStore(
            {
                isSearchable: true,
                virtualized: false,
                isMulti: true,
                actions: {
                    find: (searchTerm) => {
                        return this.routeStore.findGenres(searchTerm);
                    },
                    createOption: (name) => {
                        return this.routeStore.createGenre({ name });
                    },
                },
            },
            this.form.$("genreIds")
        );

        this.locationSelectStore = new MrbFieldSelectStore(
            {
                isSearchable: true,
                virtualized: false,
                actions: {
                    find: (searchTerm) => {
                        return this.routeStore.findLocations(searchTerm);
                    },
                    createOption: (name) => {
                        return this.routeStore.createLocation({ name });
                    },
                },
            },
            this.form.$("locationId")
        );

        this.languageSelectStore = new MrbFieldSelectStore(
            {
                isSearchable: true,
                virtualized: false,
                actions: {
                    find: (searchTerm) => {
                        return this.routeStore.findLanguages(searchTerm);
                    },
                    createOption: (name) => {
                        return this.routeStore.createLanguage({ name });
                    },
                },
            },
            this.form.$("languageId")
        );

        this.producerSelectStore = new MrbFieldSelectStore(
            {
                isSearchable: true,
                virtualized: false,
                actions: {
                    find: (searchTerm) => {
                        return this.routeStore.findProducers(searchTerm);
                    },
                    createOption: (name) => {
                        return this.routeStore.createProducer({ name });
                    },
                },
            },
            this.form.$("producerId")
        );
    }

    create(data) {
        data.coverImageId = this.coverImageUploadViewStore.uploadedFileId;
        const selectedLocationOption = this.locationSelectStore.selectedOption;
        if (selectedLocationOption) {
            data.location = selectedLocationOption.item;
        }
        const selectedLanguageOption = this.languageSelectStore.selectedOption;
        if (selectedLanguageOption) {
            data.language = selectedLanguageOption.item;
        }
        const selectedGenres = this.genreSelectStore.selectedOption;
        if (selectedGenres) {
            data.genres = map(selectedGenres, (genreOption) => genreOption.item);
        }
        const selectedProducerOption = this.producerSelectStore.selectedOption;
        if (selectedProducerOption) {
            data.producer = selectedProducerOption.item;
        }
        data.podcastCategory = this.podcastCategorySelectStore.selectedItem;
        const scheduledReleaseAt = this.form.$("scheduledReleaseAt").value || null;
        data.scheduledReleaseAt = data.isActive ? null : (scheduledReleaseAt ? new Date(scheduledReleaseAt).toISOString() : null);

        this.podcastGroupEditViewStore.addSubPodcast(data);
        return Promise.resolve();
    }

    @action.bound
    turnOffKedayoVisibility() {
        this.form.$("isVisibleInHandheldApp").onChange(false);
        this.form.$("isVisibleInHandheldPinkApp").onChange(false);
    }

    onCreateError(err) {
        this.rootStore.notificationStore.error("An unexpected error occurred while trying to update resource.", err);
    }

    resetForm() {
        this.form.clear();
        this.coverImageUploadViewStore.remove();
        const isPodcastGroupPremium = this.podcastGroupEditViewStore.form.$("isPremium").value;
        const isPodcastGroupVisibleInApp = this.podcastGroupEditViewStore.form.$("isVisibleInApp").value;
        const isPodcastGroupVisibleInNakiTouch = this.podcastGroupEditViewStore.form.$("isVisibleInEmbeddedApp").value;
        // A new sub-podcast of a Shiurim group starts hidden on the Kedayo and Kedayo Pink, whatever the group has.
        const isShiurGroup = this.podcastGroupEditViewStore.isShiurCategorySelected;
        const isPodcastGroupVisibleInHandheldPink =
            !isShiurGroup && this.podcastGroupEditViewStore.form.$("isVisibleInHandheldPinkApp").value;
        const isPodcastGroupVisibleInHandheld =
            !isShiurGroup && this.podcastGroupEditViewStore.form.$("isVisibleInHandheldApp").value;
        this.form.set({
            isActive: false,
            isTurnedOnByDefault: true,
            isTurnedOnByDefaultInApp: true,
            isTurnedOnByDefaultInEmbeddedApp: true,
            isTurnedOnByDefaultInHandheldPinkApp: true,
            isTurnedOnByDefaultInHandheldApp: true,
            isVisibleInApp: isPodcastGroupVisibleInApp,
            isVisibleInEmbeddedApp: isPodcastGroupVisibleInNakiTouch,
            isVisibleInHandheldPinkApp: isPodcastGroupVisibleInHandheldPink,
            isVisibleInHandheldApp: isPodcastGroupVisibleInHandheld,
            podcastFeed: {
                episodeSortTypeId: this.episodeSortTypeOptions[0].value,
                recentlyUpdatedVisibility: true,
            },
            isPremium: isPodcastGroupPremium,
        });
        this.genreSelectStore.setSelectedItem(null);
        this.locationSelectStore.setSelectedItem(null);
        this.languageSelectStore.setSelectedItem(null);
        this.producerSelectStore.setSelectedItem(null);
        // form.clear() empties the category value while the select still shows the old pick, so the next add
        // failed as "Category is required". Start the tab on the group's category again, as the constructor does.
        this.podcastCategorySelectStore.setSelectedItem(
            this.podcastGroupEditViewStore.podcastCategorySelectStore.selectedItem
        );
    }

    onCreateSuccess() {
        this.resetForm();
    }
}

export default AddPodcastGroupItemViewStore;
